import React, { useState } from 'react';
import { Heart, Search, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserLikes } from '../../hooks/useUserLikes';
import { useAuth } from '../auth/AuthContext';
import { SEO } from '../seo/SEO';

const ITEMS_PER_PAGE = 10;

export const LikedSearchesPage: React.FC = () => {
  const { likedSearches, toggleLike, isLoading } = useUserLikes();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(likedSearches.length / ITEMS_PER_PAGE);

  // Get current page's searches
  const indexOfLastSearch = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstSearch = indexOfLastSearch - ITEMS_PER_PAGE;
  const currentSearches = likedSearches.slice(indexOfFirstSearch, indexOfLastSearch);

  const handleUnlike = async (e: React.MouseEvent, searchId: string) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleLike(searchId);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!user) {
    return (
      <>
        <SEO 
          title="Liked Soccer Predictions | Banga.ai"
          description="Sign in to save and manage your favorite soccer predictions and betting insights. Access your personalized collection of AI-powered football analysis."
          canonicalUrl="https://banga.ai/liked-searches"
        />
        <div className="min-h-[calc(100vh-theme(spacing.32))] flex items-center justify-center px-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Sign in to view your liked searches
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Create an account or sign in to save and manage your favorite searches.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/signup"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Create Account
              </Link>
              <Link
                to="/signin"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="My Liked Soccer Predictions | Banga.ai"
        description="Access your saved soccer predictions and betting insights. View and manage your personalized collection of AI-powered football analysis."
        canonicalUrl="https://banga.ai/liked-searches"
      />
      
      <div className="container mx-auto px-0 sm:px-4 py-8">
        <div className="mb-6 px-4 sm:px-0">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-red-500" />
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Your Liked Searches
                </h1>
              </div>
              {likedSearches.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {likedSearches.length} {likedSearches.length === 1 ? 'search' : 'searches'}
                </span>
              )}
            </div>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Quick access to your favorite search queries
            </p>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                ))}
              </div>
            ) : likedSearches.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No liked searches yet
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Like your favorite searches to quickly access them later
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Start Searching
                </Link>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {currentSearches.map((item) => (
                    <Link
                      key={item.id}
                      to="/liked-searches/search"
                      state={{ query: item.query }}
                      className="block p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-gray-900 dark:text-white font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {item.query}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
                            <span>{item.searches.toLocaleString()} searches</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleUnlike(e, item.id)}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                          title="Unlike search"
                        >
                          <Heart className="w-5 h-5" fill="currentColor" />
                        </button>
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    <div className="flex space-x-1">
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        const isCurrentPage = pageNumber === currentPage;
                        const isNearCurrent = Math.abs(pageNumber - currentPage) <= 1;
                        const isEndPage = pageNumber === 1 || pageNumber === totalPages;

                        if (!isNearCurrent && !isEndPage) {
                          if (pageNumber === 2 || pageNumber === totalPages - 1) {
                            return <span key={pageNumber} className="px-2 py-1 text-gray-400 dark:text-gray-500">...</span>;
                          }
                          return null;
                        }

                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                              isCurrentPage
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Next page"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};